import React, { useEffect, useRef, useState } from "react";
import { Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Trash2, Info, X } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const COLUMN_DICTIONARY = [
  { col: "brand_name", req: true, note: "Existing brand re-used; otherwise a new brand is created automatically." },
  { col: "campaign_name", req: true, note: "Display name shown on cards and tracker." },
  { col: "campaign_type", req: true, note: "One of: ecom_x_brand · ecom_x_category · brand_only" },
  { col: "partner_1_name", req: true, note: "Primary partner (usually the e-commerce platform)." },
  { col: "partner_2_name", req: false, note: "Required when campaign_type=ecom_x_brand." },
  { col: "category_name", req: false, note: "Required when campaign_type=ecom_x_category." },
  { col: "google_sheet_url", req: true, note: "Full edit/share URL — sheet must be set to “Anyone with the link · Viewer”." },
  { col: "sheet_tab_campaign", req: false, note: "Defaults to: campaign" },
  { col: "sheet_tab_creators", req: false, note: "Defaults to: creators (use “Optins” if your sheet uses that name)." },
  { col: "sheet_tab_content", req: false, note: "Defaults to: content" },
  { col: "status", req: false, note: "Active · Draft · Pending · Completed · Expired (defaults to Pending)." },
  { col: "visible_to_brand", req: false, note: "true · false (defaults to false)." },
  { col: "team_name", req: false, note: "Existing team to attach; created under the brand if missing." },
  { col: "month", req: false, note: "Free text label e.g., NOV 2026." },
  { col: "start_date", req: false, note: "YYYY-MM-DD" },
  { col: "end_date", req: false, note: "YYYY-MM-DD" },
  { col: "deliverable", req: false, note: "Free text: e.g., 6 Reels · 12 Stories" },
  { col: "budget", req: false, note: "Numeric INR (no ₹ or commas)." },
  { col: "billing_status", req: false, note: "Paid · Pending · Overdue" },
  { col: "email_subject", req: false, note: "Free text shown on the brand campaign card." },
  { col: "brief_link", req: false, note: "Public URL to brief doc." },
  { col: "campaign_id", req: false, note: "Custom ID like CMP-ABC12345; auto-generated if blank." },
];

export default function BulkImportCard() {
  const [open, setOpen] = useState(true);
  const [preview, setPreview] = useState(null); // { summary, rows, columns, filename }
  const [committing, setCommitting] = useState(false);
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [dictOpen, setDictOpen] = useState(false);
  const inputRef = useRef(null);

  const loadBatches = async () => {
    setLoadingBatches(true);
    try {
      const { data } = await api.get("/imports/campaigns");
      setBatches(data.batches || []);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Failed to load import history");
    } finally {
      setLoadingBatches(false);
    }
  };
  useEffect(() => { loadBatches(); }, []);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/imports/campaigns/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "wishlink-campaigns-import-template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (ex) {
      toast.error("Failed to download template");
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) { toast.error("Please upload a .csv file"); return; }
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/imports/campaigns/preview", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview({ ...data, filename: file.name });
      if (data.summary?.errors > 0) toast.warning(`${data.summary.errors} of ${data.summary.total} rows have errors — fix or skip them.`);
      else toast.success(`Validated ${data.summary?.total || 0} rows — all good`);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Failed to parse CSV");
    } finally {
      e.target.value = ""; // allow re-upload of same file
    }
  };

  const cancelPreview = () => setPreview(null);

  const commit = async () => {
    if (!preview) return;
    const validRows = preview.rows.filter((r) => r.valid);
    if (validRows.length === 0) { toast.error("No valid rows to import."); return; }
    setCommitting(true);
    try {
      const { data } = await api.post("/imports/campaigns/commit", { rows: preview.rows, filename: preview.filename });
      toast.success(`Imported ${data.created_count} campaign${data.created_count === 1 ? "" : "s"}`);
      setPreview(null);
      await loadBatches();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  const deleteCampaign = async (batchId, campaignId) => {
    if (!window.confirm("Permanently delete this campaign? This action cannot be undone.")) return;
    try {
      await api.delete(`/imports/campaigns/${batchId}/items/${campaignId}`);
      toast.success("Campaign deleted");
      await loadBatches();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Delete failed");
    }
  };

  const deleteBatch = async (batchId) => {
    if (!window.confirm("Delete EVERY campaign imported in this batch? Brands/teams created exclusively by this batch will also be removed. This action cannot be undone.")) return;
    try {
      const { data } = await api.delete(`/imports/campaigns/${batchId}`);
      toast.success(`Deleted ${data.deleted_campaigns} campaign${data.deleted_campaigns === 1 ? "" : "s"}`);
      if (expandedBatch === batchId) setExpandedBatch(null);
      await loadBatches();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="wl-card-solid p-6 mb-6 max-w-5xl" data-testid="bulk-import-card">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full text-left">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <FileSpreadsheet size={16} className="text-[var(--brand)]" />
            <h2 className="font-display text-base font-semibold">Bulk import campaigns</h2>
          </div>
          <p className="text-sm text-[var(--text-secondary)]">
            Create many campaigns at once from a CSV. Each row creates one campaign — brands & teams are auto-created if they don't exist.
          </p>
        </div>
        <ChevronDown size={18} className={`text-[var(--text-secondary)] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-5 space-y-5">
          {/* Step 1 - Template */}
          <div className="border border-[var(--border-default)] rounded-lg p-4 bg-[var(--bg-tint)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[13px] font-semibold mb-1">1 · Download the template</div>
                <p className="text-xs text-[var(--text-secondary)]">22 columns covering campaign info, sheet mapping & tracking. Mandatory fields are flagged below.</p>
              </div>
              <button onClick={downloadTemplate} className="wl-btn-secondary inline-flex items-center gap-1.5 text-xs whitespace-nowrap" data-testid="bulk-download-template">
                <Download size={12} /> Download CSV template
              </button>
            </div>
            <button onClick={() => setDictOpen(!dictOpen)} className="mt-3 text-xs text-[var(--brand)] inline-flex items-center gap-1 hover:underline" data-testid="bulk-toggle-dictionary">
              <Info size={11} /> {dictOpen ? "Hide" : "View"} column dictionary ({COLUMN_DICTIONARY.length} columns)
            </button>
            {dictOpen && (
              <div className="mt-3 overflow-hidden rounded border border-[var(--border-default)] bg-white">
                <table className="wl-table">
                  <thead><tr><th>Column</th><th>Required</th><th>Notes</th></tr></thead>
                  <tbody>
                    {COLUMN_DICTIONARY.map((c) => (
                      <tr key={c.col}>
                        <td className="font-mono text-[12px]">{c.col}</td>
                        <td>
                          {c.req
                            ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-full">REQUIRED</span>
                            : <span className="text-[11px] text-[var(--text-secondary)]">optional</span>}
                        </td>
                        <td className="text-[12px]">{c.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-3 text-[11px] text-[var(--text-secondary)] bg-white border border-[var(--border-default)] rounded px-3 py-2">
              <strong>Google Sheet access:</strong> Each campaign's sheet must be shared as "Anyone with the link · Viewer" — no service-account or OAuth setup needed. Tracker data is fetched on-demand and cached for 5 minutes.
            </div>
          </div>

          {/* Step 2 - Upload */}
          <div className="border border-[var(--border-default)] rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[13px] font-semibold mb-1">2 · Upload your filled-in CSV</div>
                <p className="text-xs text-[var(--text-secondary)]">We'll validate each row before anything is created. You'll see all errors before confirming.</p>
              </div>
              <input type="file" accept=".csv,text/csv" ref={inputRef} onChange={onFile} className="hidden" data-testid="bulk-file-input" />
              <button onClick={() => inputRef.current?.click()} className="wl-btn-primary inline-flex items-center gap-1.5 text-xs whitespace-nowrap" data-testid="bulk-upload-btn">
                <Upload size={12} /> Choose CSV file
              </button>
            </div>
          </div>

          {/* Step 3 - Preview */}
          {preview && (
            <div className="border border-[var(--border-default)] rounded-lg p-4" data-testid="bulk-preview">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[13px] font-semibold">3 · Review &amp; confirm</div>
                  <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                    <span className="font-mono">{preview.filename}</span> · {preview.summary.total} row{preview.summary.total === 1 ? "" : "s"} ·
                    <span className="text-emerald-700 font-medium ml-1">{preview.summary.valid} valid</span> ·
                    <span className={`ml-1 font-medium ${preview.summary.errors ? "text-red-700" : "text-[var(--text-secondary)]"}`}>{preview.summary.errors} with errors</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={cancelPreview} className="wl-btn-ghost text-xs inline-flex items-center gap-1" data-testid="bulk-preview-cancel"><X size={12} /> Cancel</button>
                  <button onClick={commit} disabled={committing || preview.summary.valid === 0} className="wl-btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="bulk-preview-commit">
                    {committing ? "Importing…" : <><CheckCircle2 size={12} /> Confirm &amp; import {preview.summary.valid} row{preview.summary.valid === 1 ? "" : "s"}</>}
                  </button>
                </div>
              </div>
              <div className="overflow-hidden rounded border border-[var(--border-default)] max-h-96 overflow-y-auto">
                <table className="wl-table">
                  <thead className="sticky top-0 bg-white"><tr>
                    <th style={{ width: 60 }}>Row</th>
                    <th style={{ width: 100 }}>Status</th>
                    <th>Campaign</th>
                    <th>Brand</th>
                    <th>Type</th>
                    <th>Errors</th>
                  </tr></thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.row} className={r.valid ? "" : "bg-red-50/40"} data-testid={`bulk-row-${r.row}`}>
                        <td className="font-mono text-xs">{r.row}</td>
                        <td>
                          {r.valid
                            ? <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full"><CheckCircle2 size={10} /> Ready</span>
                            : <span className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full"><AlertTriangle size={10} /> Errors</span>}
                        </td>
                        <td className="text-[13px]">{r.data?.campaign_name || <span className="text-[var(--text-secondary)] italic">—</span>}</td>
                        <td className="text-[13px]">{r.data?.brand_name || <span className="text-[var(--text-secondary)] italic">—</span>}</td>
                        <td className="font-mono text-[11px] text-[var(--text-secondary)]">{r.data?.campaign_type || "—"}</td>
                        <td className="text-[12px]">
                          {r.errors.length === 0 ? <span className="text-[var(--text-secondary)]">—</span> : (
                            <ul className="space-y-1">
                              {r.errors.map((er) => (
                                <li key={`${er.column}::${er.message}`} className="text-red-700"><span className="font-mono font-semibold">{er.column}</span>: {er.message}</li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recent imports */}
          <div>
            <h3 className="text-[13px] font-semibold mb-2">Import history</h3>
            {loadingBatches ? (
              <p className="text-xs text-[var(--text-secondary)]">Loading…</p>
            ) : batches.length === 0 ? (
              <p className="text-xs text-[var(--text-secondary)]">No imports yet — once you import a CSV, you can review and undo from here.</p>
            ) : (
              <div className="space-y-2">
                {batches.map((b) => (
                  <div key={b.id} className="border border-[var(--border-default)] rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[var(--bg-tint)]">
                      <button onClick={() => setExpandedBatch(expandedBatch === b.id ? null : b.id)} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                        <ChevronRight size={14} className={`text-[var(--text-secondary)] transition-transform ${expandedBatch === b.id ? "rotate-90" : ""}`} />
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium truncate">{b.filename}</div>
                          <div className="text-[11px] text-[var(--text-secondary)]">
                            {new Date(b.uploaded_at).toLocaleString()} · by {b.uploaded_by} · {b.alive_count} live / {b.deleted_count} deleted
                          </div>
                        </div>
                      </button>
                      <button onClick={() => deleteBatch(b.id)} className="wl-btn-destructive text-[11px] inline-flex items-center gap-1 whitespace-nowrap" data-testid={`batch-delete-all-${b.id}`}>
                        <Trash2 size={11} /> Delete all from this batch
                      </button>
                    </div>
                    {expandedBatch === b.id && (
                      <div className="px-4 py-2">
                        {b.campaigns.length === 0 ? (
                          <p className="text-xs text-[var(--text-secondary)] py-2">All campaigns from this batch have been deleted.</p>
                        ) : (
                          <table className="wl-table">
                            <thead><tr><th>Campaign ID</th><th>Name</th><th>Brand</th><th>Status</th><th></th></tr></thead>
                            <tbody>
                              {b.campaigns.map((c) => (
                                <tr key={c.id}>
                                  <td className="font-mono text-[12px]">{c.campaign_id}</td>
                                  <td className="text-[13px]">{c.campaign_name}</td>
                                  <td className="text-[13px]">{c.brand_name}</td>
                                  <td className="text-[12px]">{c.status}</td>
                                  <td className="text-right">
                                    <button onClick={() => deleteCampaign(b.id, c.id)} className="wl-btn-ghost text-[11px] inline-flex items-center gap-1 text-red-700 hover:text-red-800" data-testid={`batch-delete-row-${c.id}`}>
                                      <Trash2 size={11} /> Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
